import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import MenuItemDetail from './MenuItemDetail';
import useWindowSize from '../hooks/useWindowSize';
import './MealPlan.css';
import './SharedMealPlan.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const SharedMealPlan = () => {
  const { shareToken } = useParams();
  const [mealPlan, setMealPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const { width } = useWindowSize();
  const isMobile = width <= 768;

  useEffect(() => {
    const fetchSharedMealPlan = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/shared/${shareToken}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('This shared meal plan is not available or has been disabled.');
          }
          throw new Error('Failed to load shared meal plan');
        }

        const data = await response.json();
        setMealPlan(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching shared meal plan:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSharedMealPlan();
  }, [shareToken]);

  const renderEventContent = useCallback((eventInfo) => {
    const { event } = eventInfo;
    const menuItem = event.extendedProps.menuItem;
    const mealType = event.extendedProps.mealType;
    
    if (!menuItem) {
      return (
        <div className="meal-event error">
          <div className="meal-event-content">
            <div className="meal-type">{mealType}</div>
            <div className="meal-event-title">Menu item not found</div>
          </div>
        </div>
      );
    }

    const isListView = ['listWeek', 'listMonth'].includes(eventInfo.view.type);
    
    return (
      <div className={`meal-event ${isListView ? 'list-view' : ''}`}>
        {menuItem.image && (
          <img 
            src={menuItem.image} 
            alt={menuItem.title}
            className="meal-event-image"
          />
        )}
        <div className="meal-event-content">
          <div className="meal-type">{mealType}</div>
          <div className="meal-event-title">{menuItem.title}</div>
        </div>
      </div>
    );
  }, []);

  const handleEventClick = useCallback((info) => {
    const menuItem = info.event.extendedProps.menuItem;
    if (menuItem) {
      setSelectedMenuItem(menuItem);
    }
  }, []);

  if (loading) {
    return (
      <div className="shared-meal-plan-container">
        <div className="loading">Loading shared meal plan...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shared-meal-plan-container">
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          <h2>Unable to Load Meal Plan</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!mealPlan || !mealPlan.meals) {
    return (
      <div className="shared-meal-plan-container">
        <div className="error-message">
          <i className="fas fa-calendar"></i>
          <h2>No Meals Found</h2>
          <p>This shared meal plan is empty.</p>
        </div>
      </div>
    );
  }

  const events = mealPlan.meals.map((meal) => {
    const menuItem = meal.menuItemId;
    if (!menuItem) return null;

    const mealTypeColors = {
      breakfast: '#FF6B6B',
      lunch: '#4ECDC4',
      dinner: '#45B7D1',
      snack: '#FFA07A'
    };

    return {
      id: meal._id,
      title: menuItem.title,
      start: new Date(meal.date),
      allDay: true,
      backgroundColor: mealTypeColors[meal.mealType] || '#95E1D3',
      borderColor: mealTypeColors[meal.mealType] || '#95E1D3',
      extendedProps: {
        menuItem,
        mealType: meal.mealType
      }
    };
  }).filter(event => event !== null);

  const calendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
    initialView: isMobile ? 'listWeek' : 'dayGridMonth',
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: isMobile ? 'listWeek' : 'dayGridMonth,listWeek'
    },
    buttonText: {
      listWeek: 'week',
      listMonth: 'month',
      dayGridMonth: 'month'
    },
    height: 'auto',
    eventContent: renderEventContent,
    eventClick: handleEventClick,
    eventDisplay: 'block',
    dayMaxEvents: isMobile ? 3 : 6,
    moreLinkClick: 'popover',
    displayEventTime: false,
    eventTimeFormat: {
      hour: 'numeric',
      minute: '2-digit',
      meridiem: 'short'
    }
  };

  return (
    <div className="shared-meal-plan-container">
      <div className="shared-meal-plan-header">
        <div className="header-content">
          <i className="fas fa-share-alt"></i>
          <h1>Shared Meal Plan</h1>
        </div>
        <p className="shared-info">
          This meal plan has been shared with you. You can view all the meals but cannot edit them.
        </p>
      </div>

      <div className="meal-plan-container">
        <FullCalendar
          {...calendarOptions}
          events={events}
          noEventsContent={() => 'No meals planned'}
        />
      </div>

      {selectedMenuItem && (
        <MenuItemDetail
          item={selectedMenuItem}
          onClose={() => setSelectedMenuItem(null)}
          readOnly={true}
        />
      )}
    </div>
  );
};

export default SharedMealPlan;

